/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * CoachingActionsBanner — surfaces the pending coaching actions the logged-in user must take, with a
 * direct CTA, so the login-time badge/notification always has a matching, actionable on-page element:
 *   - agreement_to_sign     (adult student) → opens the tutoring agreement to sign (signerRole=student)
 *   - guardian_consent      (guardian)      → opens the guardian consent to sign (signerRole=guardian)
 *   - add_guardian          (minor student) → link to Profile to invite a parent/guardian
 *   - resend_guardian_invite(minor student) → resend the pending guardian invite, or invite a different one
 *   - remind_guardian       (minor student) → re-notify a linked guardian to approve/sign
 * Driven by GET /api/user/coaching-actions (role-aware, server-side).
 */

import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { FileSignature, Loader2, RefreshCw, UserPlus } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../services/api'
import coachService from '../services/coachService'
import { useAuthStore } from '../stores/authStore'
import TutoringAgreementModal from './tutoring/TutoringAgreementModal'

// Sign actions open the agreement modal with the corresponding signer role.
const SIGN_ROLE = {
  agreement_to_sign: 'student',
  guardian_consent: 'guardian'
}
const CTA_LABEL = {
  agreement_to_sign: 'Sign agreement',
  guardian_consent: 'Sign consent',
  add_guardian: 'Add guardian',
  remind_guardian: 'Remind guardian',
  resend_guardian_invite: 'Resend invitation'
}

export default function CoachingActionsBanner() {
  const [items, setItems] = useState([])
  const [sign, setSign] = useState(null) // { relationshipId, role } when a sign modal is open
  const [reminding, setReminding] = useState(null) // relationshipId of an in-flight reminder
  const [resending, setResending] = useState(null) // invitationId of an in-flight resend
  // The banner fetches on its own, but mounting can race the login auth handshake — an early call
  // before the session is attached 401s and would leave the banner silently empty. Key the fetch on
  // the authenticated user so it (re)runs once the session is ready.
  const userId = useAuthStore((s) => s.user?.id)

  const load = useCallback(() => {
    let cancelled = false
    api
      .get('/user/coaching-actions')
      .then((res) => {
        if (cancelled) return
        setItems((res.data?.data?.items || []).filter((i) => CTA_LABEL[i.type]))
      })
      .catch(() => {
        if (!cancelled) setItems([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (userId) return load()
  }, [userId, load])

  const handleRemind = useCallback(async (relationshipId) => {
    setReminding(relationshipId)
    try {
      const res = await coachService.remindGuardian(relationshipId)
      toast.success(res?.message || 'Reminder sent to your guardian.')
    } catch (err) {
      toast.error(err.message || 'Could not send the reminder.')
    } finally {
      setReminding(null)
    }
  }, [])

  const handleResend = useCallback(async (invitationId) => {
    setResending(invitationId)
    try {
      const res = await coachService.resendInvitation(invitationId)
      toast.success(res?.message || 'Invitation resent.')
    } catch (err) {
      toast.error(err.message || 'Could not resend the invitation.')
    } finally {
      setResending(null)
    }
  }, [])

  if (items.length === 0) return null

  // Items that are purely informational waits (remind only) shouldn't read as hard blockers.
  const onlyWaiting = items.every((i) => i.type === 'remind_guardian')

  return (
    <>
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-md">
            <FileSignature className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {onlyWaiting
                ? 'Waiting on your guardian'
                : items.length === 1
                  ? 'Action needed to start tutoring'
                  : `${items.length} actions needed to start tutoring`}
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              {onlyWaiting
                ? 'Tutoring activates once your guardian approves. You can send them a reminder.'
                : 'Tutoring activates once the required steps are complete.'}
            </p>
          </div>
        </div>
        <div className="space-y-2">
          {items.map((item, idx) => (
            <div
              key={item.relationshipId || item.invitationId || `${item.type}-${idx}`}
              className="flex items-center justify-between gap-3 p-3 bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700 rounded-lg"
            >
              <p className="text-sm text-gray-900 dark:text-gray-100">{item.label}</p>
              {SIGN_ROLE[item.type] ? (
                <button
                  onClick={() =>
                    setSign({ relationshipId: item.relationshipId, role: SIGN_ROLE[item.type] })
                  }
                  className="btn btn-sm btn-primary whitespace-nowrap"
                >
                  {CTA_LABEL[item.type]}
                </button>
              ) : item.type === 'remind_guardian' ? (
                <button
                  onClick={() => handleRemind(item.relationshipId)}
                  disabled={reminding === item.relationshipId}
                  className="btn btn-sm btn-primary whitespace-nowrap inline-flex items-center gap-1.5 disabled:opacity-60"
                >
                  {reminding === item.relationshipId && (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  )}
                  {CTA_LABEL[item.type]}
                </button>
              ) : item.type === 'resend_guardian_invite' ? (
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleResend(item.invitationId)}
                    disabled={resending === item.invitationId}
                    className="btn btn-sm btn-primary whitespace-nowrap inline-flex items-center gap-1.5 disabled:opacity-60"
                  >
                    {resending === item.invitationId ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    {CTA_LABEL[item.type]}
                  </button>
                  <Link
                    to="/profile?action=invite-guardian#organization"
                    className="btn btn-sm btn-secondary whitespace-nowrap inline-flex items-center gap-1.5"
                  >
                    <UserPlus className="w-4 h-4" />
                    Invite a different guardian
                  </Link>
                </div>
              ) : (
                <Link
                  to={item.link || '/profile'}
                  className="btn btn-sm btn-primary whitespace-nowrap"
                >
                  {CTA_LABEL[item.type]}
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>

      <TutoringAgreementModal
        isOpen={!!sign}
        relationshipId={sign?.relationshipId}
        signerRole={sign?.role}
        onClose={() => setSign(null)}
        onSigned={() => load()}
      />
    </>
  )
}
